import {
  Box,
  Button,
  Heading,
  Image,
  Flex,
  ButtonGroup,
  Icon,
  Text,
  VStack,
  Spinner,
} from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { VscSettings } from 'react-icons/vsc';
import { HoveringBox } from '~/components/ui/motion/Box';
import { ICONS } from '~/constants';
import { useAgency } from '~/hooks/useAgency';
import { useUIStore } from '~/store';
import { useUserAction } from '~/hooks/useUserAction';
import { useFilters } from '~/hooks/useFilters';
import { getTabLabelFromName } from '@habity/services';
import type { SearchTab } from '@habity/types';

export const NoResults = ({ tab }: { tab: string }) => {
  const t = useTranslations('HomesList.NoResults');
  const setModals = useUIStore((state) => state.setModals);
  const modals = useUIStore((state) => state.modals);
  const searchJustCreated = useUIStore((state) => state.searchJustCreated);
  const setSearchJustCreated = useUIStore(
    (state) => state.setSearchJustCreated
  );
  const { getIllustrationsRoute, settings } = useAgency();
  const { utility } = settings || {};
  const { showIllustrations } = utility || {};
  const { executeAction, isLoading: isExecutingAction } = useUserAction();
  const { filters } = useFilters();

  const isHomesTab = tab === 'homes';
  const label = getTabLabelFromName(tab as SearchTab).toLowerCase();

  return (
    <Flex minHeight="70vh" direction="column" justifyContent="center">
      {showIllustrations && (
        <HoveringBox width="25%">
          <Image
            src={getIllustrationsRoute('no-results.svg')}
            alt="No results"
          />
        </HoveringBox>
      )}

      <Box pb={40}>
        {isHomesTab && searchJustCreated ? (
          <VStack spacing="4">
            <Heading textAlign="center" size="md" color="blackAlpha.900">
              Perfekt! Din bevakning är skapad
            </Heading>
            <Text
              textAlign="center"
              color="blackAlpha.600"
              maxW="500px"
              fontSize="md"
            >
              Du kommer få ett mejl så fort en bostad som matchar dina kriterier
              kommer ut på marknaden. Vill du bevaka något mer?
            </Text>
            <Box textAlign="center" marginTop={4}>
              <ButtonGroup>
                <Button
                  variant="primary"
                  leftIcon={<Icon as={ICONS.HOMEBUYERS_ALERT} />}
                  onClick={() => {
                    setSearchJustCreated(false);
                    setModals({ ...modals, propertySearchOpen: true });
                  }}
                  size={{ base: 'md', md: 'lg' }}
                >
                  Skapa ny bevakning
                </Button>
                <Button
                  variant="outline"
                  leftIcon={<VscSettings />}
                  onClick={() => setModals({ ...modals, searchOpen: true })}
                  size={{ base: 'md', md: 'lg' }}
                >
                  Justera sökning
                </Button>
              </ButtonGroup>
            </Box>
          </VStack>
        ) : (
          <VStack spacing="4">
            <Heading textAlign="center" size="md" color="blackAlpha.900">
              Inga {label} just nu
            </Heading>
            <Text
              textAlign="center"
              color="blackAlpha.600"
              maxW="500px"
              fontSize="md"
            >
              {isHomesTab
                ? 'Vill du få notiser när det finns nya bostäder för den här bevakningen?'
                : 'Prova att ändra din sökning eller dina filter.'}
            </Text>
            {isHomesTab && (
              <Box textAlign="center" marginTop={4}>
                <Button
                  variant="secondary"
                  leftIcon={
                    isExecutingAction ? (
                      <Spinner size="sm" />
                    ) : (
                      <Icon as={ICONS.HOMEBUYERS_ALERT} />
                    )
                  }
                  onClick={() => {
                    executeAction({
                      action: 'created_search_profile',
                      searchFilters: filters || undefined,
                    });
                  }}
                  size={{ base: 'md', md: 'lg' }}
                  isDisabled={isExecutingAction}
                >
                  {isExecutingAction ? 'Skapar bevakning...' : 'Ja, tack!'}
                </Button>
              </Box>
            )}
          </VStack>
        )}
      </Box>
    </Flex>
  );
};
